import { useEffect, useState } from "react";
import { AxiosInstance } from "axios";
import { PremFixture } from "../models";
import { FixtureResponse, FixtureResponseStatEntry } from "../models/api";
import { fetchBootstrap, fetchFixtures } from "./api";
import { currentEvent } from "./bootstrap";

type NamedPlayer = { id: number; name: string };

const describe = (entries: FixtureResponseStatEntry[], players: NamedPlayer[], spaced: boolean) =>
	entries
		.map((a) => {
			const player = players.find((p) => p.id === a.element);
			return player?.name + (a.value > 1 ? `${spaced ? " " : ""}(${a.value})` : "");
		})
		.join(", ");

const orNull = (s: string) => (s.length === 0 ? null : s);

export const premFixturesFrom = (fixtures: FixtureResponse[], teams: { id: number; name: string }[], players: NamedPlayer[]): PremFixture[] =>
	fixtures.map((f) => {
		const homeTeam = teams.find((x) => x.id === f.team_h);
		const awayTeam = teams.find((x) => x.id === f.team_a);

		const goals = f.stats.find((x) => x.identifier === "goals_scored");
		const ownGoals = f.stats.find((x) => x.identifier === "own_goals");
		const assists = f.stats.find((x) => x.identifier === "assists");

		// an own goal counts for the other side
		const homeGoalsArr = (goals?.h ?? []).concat(ownGoals?.a ?? []);
		const awayGoalsArr = (goals?.a ?? []).concat(ownGoals?.h ?? []);

		return {
			homeTeam: homeTeam?.name ?? "Unknown",
			awayTeam: awayTeam?.name ?? "Unknown",
			homeGoals: f.team_h_score,
			awayGoals: f.team_a_score,
			minutes: f.minutes,
			live: f.started && !f.finished_provisional,
			finished: f.finished_provisional,
			start: f.kickoff_time,
			homeGoalsDesc: orNull(describe(homeGoalsArr, players, false)),
			awayGoalsDesc: orNull(describe(awayGoalsArr, players, false)),
			homeAssistsDesc: orNull(describe(assists?.h ?? [], players, true)),
			awayAssistsDesc: orNull(describe(assists?.a ?? [], players, false)),
		};
	});

export const usePremFixtures = (api: AxiosInstance) => {
	const [loading, setLoading] = useState(true);
	const [loadingMessage, setLoadingMessage] = useState("Fetching Gameweek");
	const [error, setError] = useState<string | null>(null);
	const [fixtures, setFixtures] = useState<PremFixture[]>([]);

	useEffect(() => {
		let cancelled = false;
		fetchBootstrap(api)
			.then((bootstrap) => {
				const weekId = currentEvent(bootstrap)?.id;
				if (weekId == null || cancelled) {
					return;
				}
				setLoadingMessage("Fetching League Fixtures");
				const teams = bootstrap.teams.map((t) => ({ id: t.id, name: t.name }));
				const players = bootstrap.elements.map((p) => ({ id: p.id, name: p.web_name }));
				return fetchFixtures(api, weekId)
					.then((data) => {
						if (cancelled) return;
						setFixtures(premFixturesFrom(data, teams, players));
						setLoading(false);
					})
					.catch(() => {
						if (cancelled) return;
						setError("Failed to fetch fixtures.");
						setLoading(false);
					});
			})
			.catch(() => {
				if (cancelled) return;
				setError("Failed to load data. Please refresh.");
				setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [api]);

	return { loading, loadingMessage, error, fixtures };
};
